import {
  useAdsBatchTemplateize,
  type AdsBatchTemplateizePhase,
} from "@/components/ads-assistant/useAdsBatchTemplateize";
import { AdsBatchTemplateizeSelectStep } from "@/components/ads-assistant/AdsBatchTemplateizeSelectStep";
import { AdsBatchTemplateizeReviewStep } from "@/components/ads-assistant/AdsBatchTemplateizeReviewStep";

function phaseLabel(phase: AdsBatchTemplateizePhase): string {
  switch (phase) {
    case "select":
      return "Pick rows";
    case "running":
      return "Asking AI…";
    case "review":
      return "Review proposals";
    case "applying":
      return "Saving…";
    case "done":
      return "Done";
  }
}

export function AdsAssistantBatchTemplateizeDialog(props: {
  vm: ReturnType<typeof useAdsBatchTemplateize>;
}) {
  const { vm } = props;
  if (!vm.open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-labelledby="ads-batch-templateize-heading"
    >
      <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-2xl border border-gray-200 bg-white p-4 shadow-xl dark:border-gray-700 dark:bg-zinc-900">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h4
              id="ads-batch-templateize-heading"
              className="text-sm font-semibold text-gray-900 dark:text-gray-100"
            >
              Batch templateize untemplated posts
            </h4>
            <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
              {phaseLabel(vm.phase)} ·{" "}
              {vm.summary.total > 0
                ? `${vm.summary.total} selected`
                : `${vm.totalUntemplated ?? "—"} untemplated total`}
            </p>
          </div>
          <button type="button" className="text-xs text-gray-500 hover:underline" onClick={vm.closeDialog}>
            Close
          </button>
        </div>

        {vm.loadError ? (
          <p className="mt-3 rounded-lg border border-red-200 bg-red-50 px-2 py-1.5 text-xs text-red-900">
            {vm.loadError}
          </p>
        ) : null}
        {vm.generalError ? (
          <p className="mt-3 rounded-lg border border-red-200 bg-red-50 px-2 py-1.5 text-xs text-red-900">
            {vm.generalError}
          </p>
        ) : null}

        {vm.phase === "select" ? <AdsBatchTemplateizeSelectStep vm={vm} /> : null}
        {vm.phase === "running" ? <RunningView vm={vm} /> : null}
        {vm.phase === "review" ? <AdsBatchTemplateizeReviewStep vm={vm} /> : null}
        {vm.phase === "applying" || vm.phase === "done" ? <ApplyDoneView vm={vm} /> : null}
      </div>
    </div>
  );
}

function RunningView(props: { vm: ReturnType<typeof useAdsBatchTemplateize> }) {
  const { vm } = props;
  const processed = vm.summary.proposed + vm.summary.skipped + vm.summary.errors;
  return (
    <div className="mt-3">
      <p className="text-sm text-gray-700 dark:text-gray-200">
        Asking AI… <span className="tabular-nums">{processed}</span> /{" "}
        <span className="tabular-nums">{vm.summary.total}</span>
      </p>
      <div className="mt-2 h-1.5 w-full rounded-full bg-gray-200 dark:bg-zinc-700">
        <div
          className="h-1.5 rounded-full bg-violet-500 transition-all"
          style={{ width: `${vm.summary.total ? (processed / vm.summary.total) * 100 : 0}%` }}
        />
      </div>
      <p className="mt-2 text-[11px] text-gray-500 dark:text-gray-400">
        One request per row, paced to avoid OpenAI rate limits. Don&apos;t close the dialog until this finishes.
      </p>
    </div>
  );
}

function ApplyDoneView(props: { vm: ReturnType<typeof useAdsBatchTemplateize> }) {
  const { vm } = props;
  const processed = vm.summary.applied + vm.summary.errors;
  const totalAttempted =
    vm.summary.applied + vm.summary.errors + (vm.phase === "applying" ? vm.summary.accepted : 0);
  if (vm.phase === "applying") {
    return (
      <div className="mt-3">
        <p className="text-sm text-gray-700 dark:text-gray-200">
          Saving… <span className="tabular-nums">{processed}</span> /{" "}
          <span className="tabular-nums">{totalAttempted}</span>
        </p>
        <div className="mt-2 h-1.5 w-full rounded-full bg-gray-200 dark:bg-zinc-700">
          <div
            className="h-1.5 rounded-full bg-emerald-500 transition-all"
            style={{ width: `${totalAttempted ? (processed / totalAttempted) * 100 : 0}%` }}
          />
        </div>
      </div>
    );
  }
  return (
    <div className="mt-3">
      <p className="text-sm font-medium text-emerald-900 dark:text-emerald-200">
        Applied <span className="tabular-nums">{vm.summary.applied}</span> template assignment
        {vm.summary.applied === 1 ? "" : "s"}.
        {vm.summary.errors ? ` ${vm.summary.errors} row${vm.summary.errors === 1 ? "" : "s"} hit an error.` : ""}
      </p>
      <div className="mt-3 flex justify-end">
        <button
          type="button"
          className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700"
          onClick={vm.closeDialog}
        >
          Close
        </button>
      </div>
    </div>
  );
}
